/********************************************* ASSETS *********************************************/
const ArrowImg = require('../assets/img/icons/ic_dropdown_arrow_rounded.svg');

/******************************************** STYLING *********************************************/
const s = require('./shapes.scss');

/******************************************** IMPORTS *********************************************/
import React from 'react';
import cn from 'classnames';
import {omit, isFalse} from 'mad-utils/lib/react';
import {observer} from 'mobx-react';
import {switchExpr} from 'mad-utils/lib/react';
import {translate} from '@canimmunize/core-imports';

/**************************************************************************************************
 *                                                                                                *
 *                                            EXPORTS                                             *
 *                                                                                                *
 **************************************************************************************************/

/********************************** TINY ARROW (SolidMiniArrow) ***********************************/
interface ArrowProps {
    direction: 'up' | 'down' | 'left' | 'right';
    center?: boolean;
    mergeClasses?: string;
    style?: React.CSSProperties;
}

/**
 * Create tiny triangular arrow pointing in the given direction
 * @param {string} direction - Must be up, down, left, or right
 * @param {boolean} center - If false, uncenter the element (defaults to center)
 * @param {string} mergeClasses - string of classes to add to the div
 */
export const SolidMiniArrow = observer(
    ({center, direction, mergeClasses, style}: ArrowProps): JSX.Element => {
        const doCenter = typeof center === `undefined` ? true : center;
        return (
            <img
                src={ArrowImg}
                className={cn(s[`angle-${direction}`], mergeClasses)}
                style={style || {}}
            />
        );
    }
);

SolidMiniArrow.displayName = `SolidMiniArrow`;

/*********************** HollowCircle (E.G. FOR CIRCULAR PSEUDO-CHECKBOXES) ***********************/
type HollowCircleArgs = React.HTMLAttributes<HTMLDivElement> & {
    colour?: string;
    thickness?: string | number;
    mergeCssClasses?: string;
    diameter?: string | number;
};

// Defaults for hollow circle
const HollowCircleDefault = {
    size: 25,
    thickness: 1,
    borderColor: `#666666`,
};

/**
 * Create a hollow circle. Defaults to gray colour, 25px diameter, and 2px
 * thickness
 * @example output:  ⭕
 */
export const HollowCircle = observer(
    ({colour, thickness, mergeCssClasses, diameter, ...props}: HollowCircleArgs) => (
        <div
            style={{
                width: diameter || HollowCircleDefault.size,
                height: diameter || HollowCircleDefault.size,
                borderColor: colour || HollowCircleDefault.borderColor,
                borderWidth: thickness || HollowCircleDefault.thickness,
            }}
            className={cn(s[`hollow-circle`], mergeCssClasses)}
            {...props}
        />
    )
);

HollowCircle.displayName = `HollowCircle`;

/******************************************** Chevron *********************************************/
const directionAngleMap = {left: 180, up: 270, right: 0, down: 90};

type ChevronAngles = 0 | 45 | 90 | 135 | 180 | 225 | 270 | 315 | keyof typeof directionAngleMap;

const chevronAngleDefault = 0;

/**
 * Creates chevron shape at given rotation
 * e.g. looks like (if rotation=`right`):
 *     >
 */
export const Chevron = observer(
    ({
        rotation = 0,
        colour,
        style,
        className,
        ...props
    }: {
        rotation?: ChevronAngles;
        colour?: string;
        style?: React.CSSProperties;
        className?: string;
        props?: Array<React.HTMLAttributes<HTMLElement>>;
    }) => {
        const cleanAngle =
            typeof rotation === `number`
                ? rotation
                : typeof rotation === `string`
                    ? directionAngleMap[rotation]
                    : chevronAngleDefault;
        return (
            <i
                className={cn(s[`chevron-right`], className || ``)}
                style={{
                    transform: `rotate(${cleanAngle}deg)`,
                    ...style,
                    ...(colour ? {backgroundColor: colour} : {}),
                }}
                {...omit(props, [`className`, `style`, `colour`, `rotation`])}
            />
        );
    }
);

Chevron.displayName = `Chevron`;

/********************************************* Circle *********************************************/
type TextOrChildren =
    | {text: string; children?: ReactChildren}
    | {text?: string; children: ReactChildren | string};

// tslint:disable-next-line: class-name
export interface _CircleProps {
    bgColourClass?: string;
    textColourClass?: string;
    diameter: number | string;
    textStyle?: React.CSSProperties;
    className?: string;
}

export const Circle = observer((props: _CircleProps & TextOrChildren) => (
    <div
        className={cn(
            `br-200`,
            `flex row center ai-center`,
            `pl-5 pr-5 ta-center`,
            !props.textStyle && `fs-15 bold helvetica-neue`,
            props.bgColourClass ? props.bgColourClass : `bg-black`,
            props.textColourClass ? props.textColourClass : `white`,
            props.className
        )}
        style={{
            width: props.diameter,
            minWidth: props.diameter,
            maxWidth: props.diameter,
            height: props.diameter,
            minHeight: props.diameter,
            maxHeight: props.diameter,
            ...props.textStyle,
        }}
    >
        {props.text || props.children}
    </div>
));

Circle.displayName = `Circle`;

/******************************************** SolidDot ********************************************/
interface DotProps {
    color: string | false;
    diameterInPx?: number;
    className?: string;
    style?: React.CSSProperties;
    /**
     * Title to display on hover
     * Falls back to value given in color
     */
    status?: string;
}

const isReceived = (status: string | false) =>
    status === `ok` || status === `okay` || status === `received`;

const isApproaching = (status: string | false) =>
    status === `warn` || status === `approaching` || status === `warning`;

const notApproaching = (status: string | false) =>
    status === `ignored` || status === `ignore` || status === `future`;

const isOverdue = (status: string | false) => status === `overdue`;

// prettier-ignore
/**
 * Map colour to given colour value
 */
const mapColor = (color: string | false): string =>
    switchExpr(
        isReceived(color),     `#65c76b`,
        isApproaching(color),  `#f5a623`,
        isOverdue(color),      `#db242a`,
        notApproaching(color), `#acacac`,
                               color
    );

/**
 * Map status to given displayable status value
 */
const buildStatus = (status: string | false): string => {
    const statusID = status ? status.replace(/^text\./gi, ``).toLowerCase() : false;

    const newStatus = switchExpr(
        isReceived(statusID),
        `dose_received`,
        isApproaching(statusID),
        `notification_approaching`,
        statusID
    );

    return newStatus ? translate(newStatus).capitalize : undefined;
};

const defDotDiameter = 8;
const defDotColor = `green`;

/**
 * Small coloured "dot" (a tiny filled circle)
 */
export const SolidDot = observer(({color, diameterInPx, className, status, style}: DotProps) => {
    const diameter = typeof diameterInPx === `undefined` ? defDotDiameter : diameterInPx;
    const radius = diameter && Math.floor(diameter / 2);
    const cleanColor = mapColor(color);

    return (
        <div
            className={cn(`align-self-center`, className || ``)}
            style={{
                backgroundColor: isFalse(color) ? null : cleanColor || defDotColor,
                width: diameter,
                minWidth: diameter,
                height: diameter,
                minHeight: diameter,
                borderRadius: radius,
                ...style,
            }}
            title={buildStatus(status || color)}
        />
    );
});

SolidDot.displayName = `SolidDot`;
